import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Stack } from "@mui/material";
import { Clock, User, Timer } from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { useTheme as useCustomTheme } from "../theme/useTheme";
import { Button } from "./Button";
import { useAuth } from "./contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FEATURE_HOUR_PACKAGE, hasAgencyFeature } from "../utils/agencyFeatures";
import { BUY_HOURS_PATH } from "../Pages/Settings/settingsPaths";
import { formatMinutes } from "../utils/numberFormat";
import { BuyHoursDialog } from "../Pages/Plan/BuyHoursDialog";
import { BILLING_HOURS_QUERY_KEY, getHoursSummary, purchaseHours } from "../services/billingService";
import { useHoursCheckoutReturn } from "../hooks/useHoursCheckoutReturn";
import { useStripeRedirect } from "../hooks/useStripeRedirect";
import { hoursExpectation } from "../hooks/stripeReturn";

const HOUR_PACKAGE_NOTE =
  "Las horas base se reinician cada 6 meses (1 de enero y 1 de julio). El consumo usa primero horas base y, " +
  "cuando se agotan, horas compradas.";

// Tiempo registrado de un ticket y saldo de horas de la agencia; el admin puede comprar horas desde aquí.
export const TimeLogCard = ({
  timeLog,
  agencyBalance,
  agencyId = null,
  inlinePurchase = false,
  previewBalance = null,
  showEmptyTimeLogMessage = true,
  showBalanceSeparator = true,
  showBalanceFootnoteMarkers = false,
  // Sobre el fondo gris de una página va en blanco; dentro de un modal, en gris
  onPageBackground = false,
}) => {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const { go: goToStripe, detailsDialog } = useStripeRedirect();

  const remainingMinutes = agencyBalance?.remaining_minutes ?? 0;
  const baseRemainingMinutes = agencyBalance?.base_remaining_minutes ?? 0;
  const purchasedRemainingMinutes = agencyBalance?.purchased_remaining_minutes ?? 0;
  const previewRemainingMinutes = previewBalance?.remaining_minutes ?? null;
  const previewBaseRemainingMinutes =
    previewBalance?.base_remaining_minutes ?? null;
  const previewPurchasedRemainingMinutes =
    previewBalance?.purchased_remaining_minutes ?? null;
  const effectiveRemainingMinutes =
    previewRemainingMinutes ?? remainingMinutes;
  const isLowBalance = effectiveRemainingMinutes < 60; // Menos de 1 hora
  const canPurchase =
    currentUser?.role === "agency_admin" && !!agencyId;
  // Sin bono (externas) no hay horas base: solo compradas
  const hasHourPackage = hasAgencyFeature(currentUser, FEATURE_HOUR_PACKAGE);

  const { data: hoursSummary } = useQuery({
    queryKey: BILLING_HOURS_QUERY_KEY,
    queryFn: getHoursSummary,
    enabled: purchaseModalOpen,
    // The Checkout baseline is read from here
    staleTime: 0,
  });

  useHoursCheckoutReturn({
    enabled: inlinePurchase,
    notifyCancel: true,
    onDone: () => queryClient.invalidateQueries({ queryKey: ["getData", `agencies/${agencyId}/`] }),
  });

  const openPurchaseModal = () => {
    if (!inlinePurchase) {
      navigate(BUY_HOURS_PATH);
      return;
    }
    setPurchaseModalOpen(true);
  };

  if (!timeLog && !agencyBalance && !canPurchase) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "8px",
        backgroundColor: onPageBackground
          ? theme.palette.background.paper
          : neutrals.gray[50],
        border: `1px solid ${neutrals.gray[200]}`,
      }}
    >
      {timeLog ? (
        <>
          {/* Fila superior: Tiempo registrado a la izquierda, Registrado por a la derecha */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Timer size={16} color={theme.palette.text.secondary} />
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatMinutes(timeLog.minutes_consumed)}
              </Typography>
            </Box>
            {timeLog.logged_by && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <User size={12} color={theme.palette.text.secondary} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Registrado por:{" "}
                  {timeLog.logged_by.full_name ||
                    timeLog.logged_by.email}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Descripción del trabajo si existe */}
          {timeLog.work_description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              {timeLog.work_description}
            </Typography>
          )}
        </>
      ) : (
        showEmptyTimeLogMessage && (
          <Typography variant="body2" color="text.secondary">
            No hay tiempo registrado aún
          </Typography>
        )
      )}

      {/* Separador y horas disponibles restantes */}
      {agencyBalance && (
        <>
          <Box
            sx={{
              mt: showBalanceSeparator ? 2 : 0.75,
              pt: showBalanceSeparator ? 2 : 0,
              borderTop: showBalanceSeparator
                ? `1px solid ${neutrals.gray[200]}`
                : "none",
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Clock size={16} color={theme.palette.text.secondary} />
            <Typography variant="body1" color="text.secondary">
              Horas disponibles restantes:{" "}
            </Typography>
            {previewRemainingMinutes !== null &&
              previewRemainingMinutes !== remainingMinutes ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.error.main,
                    textDecoration: "line-through",
                  }}
                >
                  {formatMinutes(remainingMinutes)}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: isLowBalance ? theme.palette.error.main : "inherit",
                  }}
                >
                  {formatMinutes(previewRemainingMinutes)}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: isLowBalance ? theme.palette.error.main : "inherit",
                }}
              >
                {formatMinutes(remainingMinutes)}
              </Typography>
            )}
          </Box>
          <Box sx={{ mt: 1, pl: "24px" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                rowGap: "6px",
                columnGap: "12px",
              }}
            >
              {hasHourPackage && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    {`Base disponibles${showBalanceFootnoteMarkers ? "¹" : ""}`}
                  </Typography>
                  {previewBaseRemainingMinutes !== null &&
                    previewBaseRemainingMinutes !== baseRemainingMinutes ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.error.main,
                          textDecoration: "line-through",
                        }}
                      >
                        {formatMinutes(baseRemainingMinutes)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatMinutes(previewBaseRemainingMinutes)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatMinutes(baseRemainingMinutes)}
                    </Typography>
                  )}
                </>
              )}
              <Typography variant="caption" color="text.secondary">
                {`Compradas disponibles${showBalanceFootnoteMarkers ? "²" : ""}`}
              </Typography>
              {previewPurchasedRemainingMinutes !== null &&
                previewPurchasedRemainingMinutes !==
                purchasedRemainingMinutes ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.error.main,
                      textDecoration: "line-through",
                    }}
                  >
                    {formatMinutes(purchasedRemainingMinutes)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatMinutes(previewPurchasedRemainingMinutes)}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatMinutes(purchasedRemainingMinutes)}
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary">
                Total disponibles
              </Typography>
              {previewRemainingMinutes !== null &&
                previewRemainingMinutes !== remainingMinutes ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.error.main,
                      textDecoration: "line-through",
                    }}
                  >
                    {formatMinutes(remainingMinutes)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: isLowBalance ? theme.palette.error.main : "inherit",
                    }}
                  >
                    {formatMinutes(previewRemainingMinutes)}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: isLowBalance ? theme.palette.error.main : "inherit",
                  }}
                >
                  {formatMinutes(remainingMinutes)}
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}

      {canPurchase && (
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${neutrals.gray[200]}`,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button onClick={openPurchaseModal}>
            Comprar horas
          </Button>
        </Box>
      )}

      {purchaseModalOpen && hoursSummary && (
        <BuyHoursDialog
          hours={hoursSummary}
          note={hasHourPackage ? HOUR_PACKAGE_NOTE : null}
          onClose={() => setPurchaseModalOpen(false)}
          onCheckout={(quantity) =>
            goToStripe(() => purchaseHours(quantity), hoursExpectation(quantity, hoursSummary.remaining_minutes))
          }
        />
      )}
      {detailsDialog}
    </Box>
  );
};

const balanceShape = PropTypes.shape({
  remaining_minutes: PropTypes.number,
  base_remaining_minutes: PropTypes.number,
  purchased_remaining_minutes: PropTypes.number,
});

TimeLogCard.propTypes = {
  timeLog: PropTypes.shape({
    minutes_consumed: PropTypes.number,
    work_description: PropTypes.string,
    logged_by: PropTypes.shape({ full_name: PropTypes.string, email: PropTypes.string }),
  }),
  agencyBalance: balanceShape,
  // Sin agencia no se ofrece la compra
  agencyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Compra con Stripe en el propio modal (Ajustes › Horas de tickets); si no, lleva a Ajustes
  inlinePurchase: PropTypes.bool,
  onPageBackground: PropTypes.bool,
  // Saldo tras registrar el tiempo que se está editando, para mostrar antes/después
  previewBalance: balanceShape,
  showEmptyTimeLogMessage: PropTypes.bool,
  showBalanceSeparator: PropTypes.bool,
  showBalanceFootnoteMarkers: PropTypes.bool,
};
